import {ResidentEmailRecipient} from '../types/email-data.types';
import {Group} from '../types/data.types';
import {EmailService} from './email.service';

export class FilterRecipients {
    residents: Array<ResidentEmailRecipient> = [];
    filters: Array<FilterRecipientsItem> = [];
    searchResidentTerm = '';
    searchBuildingTerm = '';
    showSelected = false;
    ignoreWaiverSettings = -1;
    orderBy = '';
    filteredResidents: Array<ResidentEmailRecipient> = [];

  constructor(public es: EmailService) {
        this.filters = this.es.buildings
            .map(b => new FilterRecipientsItem(b.id, b.name, this.es.groups, this, b.cNSettings))
            .filter(item => item.isCanEdit);
    }

    getFilterState(): FilterStateItem[] {
        const state = [];
        this.filters.forEach(f => {
            const checkedResidents = this.residents
                .filter(r => (r.checked && r.buildingId === f.buildingId))
                .map(r => r.id);
            if (!checkedResidents.length) return;
            state.push({
                buildingId: f.buildingId,
                buildingName: f.buildingName,

                tenantsChecked: f.tenantsChecked,
                tenantsHalfChecked: f.isHalfCheckedClass('rent'),
                tenantsCount: f.getUsersCheckedCount('rent'),

                ownerOnChecked: f.ownerOnChecked,
                ownerOnHalfChecked: f.isHalfCheckedClass('owner_on'),
                ownerOnCount: f.getUsersCheckedCount('owner_on'),

                ownerOffChecked: f.ownerOffChecked,
                ownerOffHalfChecked: f.isHalfCheckedClass('owner_off'),
                ownerOffCount: f.getUsersCheckedCount('owner_off'),

                residentOwnerChecked: f.residentOwnerChecked,
                residentOwnerHalfChecked: f.isHalfCheckedClass('resident_owner'),
                residentOwnerCount: f.getUsersCheckedCount('resident_owner'),

                residentOnlyNotOnDeedChecked: f.residentOnlyNotOnDeedChecked,
                residentOnlyNotOnDeedHalfChecked: f.isHalfCheckedClass('resident_only_not_on_deed'),
                residentOnlyNotOnDeedCount: f.getUsersCheckedCount('resident_only_not_on_deed'),

                ownerFamilyChecked: f.ownerFamilyChecked,
                ownerFamilyHalfChecked: f.isHalfCheckedClass('owner_family'),
                ownerFamilyCount: f.getUsersCheckedCount('owner_family'),

                guestChecked: f.guestChecked,
                guestHalfChecked: f.isHalfCheckedClass('guest'),
                guestCount: f.getUsersCheckedCount('guest'),

                offsiteResidentChecked: f.offsiteResidentChecked,
                offsiteResidentHalfChecked: f.isHalfCheckedClass('offsite_resident'),
                offsiteResidentCount: f.getUsersCheckedCount('offsite_resident'),

                residentChecked: f.residentChecked,
                residentHalfChecked: f.isHalfCheckedClass('resident'),
                residentCount: f.getUsersCheckedCount('resident'),

                occupantChecked: f.occupantChecked,
                occupantHalfChecked: f.isHalfCheckedClass('occupant'),
                occupantCount: f.getUsersCheckedCount('occupant'),

                notSetChecked: f.notSetChecked,
                notSetHalfChecked: f.isHalfCheckedClass('unknown'),
                notSetCount: f.getUsersCheckedCount('unknown'),

                groups: f.getGroupFilterState(),
                checkedResidents: checkedResidents,

                staffChecked: f.staffChecked,
                staffHalfChecked: f.isHalfCheckedClass('staff'),
                staffCount: f.getUsersCheckedCount('staff'),

                unitManagerChecked: f.unitManagerChecked,
                unitManagerHalfChecked: f.isHalfCheckedClass('unit_manager'),
                unitManagerCount: f.getUsersCheckedCount('unit_manager'),
            });
        });
        return state;
    }

    public updateFilteredResidentsList(): void {
        this.filteredResidents = this.getFilteredResidents();
    }

    /**
     * Returns filtered residents list
     */
    private getFilteredResidents(): ResidentEmailRecipient[] {
        function searchCondition(searchTerm = '', resident: ResidentEmailRecipient) {
            if (!searchTerm) {
                return true;
            }

            searchTerm = searchTerm.toLowerCase();

            // Feature for suiteNumber field only
            if (searchTerm.includes('~') || searchTerm.includes('*')) {
                const replacement = {
                    '*': '.*',
                    '~': '.{1}'
                };

                const regex = searchTerm.split('').map(char => replacement[char] || char).join('');

                return new RegExp(`^${regex}$`, 'i').test(resident.suiteNumber);
            }

            const sourceStr = `${resident.fullName} ${resident.email} ${resident.buildingName} ${resident.suiteNumber}`.toLowerCase();

            return sourceStr.includes(searchTerm);
        }

        function filterCondition(filter: FilterRecipientsItem, resident: ResidentEmailRecipient) {
            const showedGroups = filter.groups.filter(g => g.show);

            return (
                (filter.tenantsShow && resident.role === 'rent') ||
                (filter.ownerOnShow && resident.role === 'owner_on') ||
                (filter.ownerOffShow && resident.role === 'owner_off') ||
                (filter.residentOwnerShow && resident.role === 'resident_owner') ||
                (filter.residentOnlyNotOnDeedShow && resident.role === 'resident_only_not_on_deed') ||
                (filter.ownerFamilyShow && resident.role === 'owner_family') ||
                (filter.guestShow && resident.role === 'guest') ||
                (filter.offsiteResidentShow && resident.role === 'offsite_resident') ||
                (filter.residentShow && resident.role === 'resident') ||
                (filter.occupantShow && resident.role === 'occupant') ||
                (filter.notSetShow && resident.role === 'unknown') ||
                (
                    resident.groups.length && showedGroups.length &&
                    resident.groups.some(residentGroup => {
                        return showedGroups.some(showedGroup => showedGroup.name === residentGroup.itemName);
                    })
                ) ||
              (filter.staffShow && resident.role === 'staff') ||
              (filter.unitManagerShow && resident.role === 'unit_manager')
            );
        }

        if (this.showSelected && this.isSomeChecked()) {

            return this.residents.filter(r => r.checked);
        }

        return this.residents.filter((resident: ResidentEmailRecipient) => {
            let condition = false;
            this.filters.forEach((filter: FilterRecipientsItem) => {
                if (
                    filter.buildingId === resident.buildingId &&
                    ((this.ignoreWaiverSettings === -1) ||
                     (resident.emailWaiverSigned && this.ignoreWaiverSettings === 1) ||
                     (!resident.emailWaiverSigned && this.ignoreWaiverSettings === 0)) &&
                    searchCondition(this.searchResidentTerm, resident) &&
                    filterCondition(filter, resident)
                ) condition = true;
            });
            return condition;
        });
    }

    toggleCheckedAll(): void {
        const isCheckedAll = this.isCheckedAll();
        this.filteredResidents.map(item => item.checked = !isCheckedAll);
        this.updateFilteredResidentsList();
    }

    uncheckAllResidents(): void {
        this.showSelected = false;
        this.residents.map(r => r.checked = false);
        this.filters.forEach(filter => filter.forceReset());
        this.updateFilteredResidentsList();
    }

    uncheckWithoutWaiverSettings(): void {
        this.residents.map(r => (!r.emailWaiverSigned) && (r.checked = false));
        this.updateCheckboxState();
    }

    isCheckedAll(): boolean {
        return this.filteredResidents.every(r => r.checked);
    }

    isSomeChecked(): boolean {
        return this.residents.some(r => r.checked);
    }

    selectedCount(): number {
        return this.residents.filter(r => r.checked).length;
    }

    getBuildingsContainCheckedUser(): number[] {
        return this.es.buildings.filter(b => {
            return this.residents.filter(r => r.buildingId === b.id).some(r => r.checked);
        }).map(b => +b.id);
    }

    updateCheckboxState() {
        this.filters.forEach(f => {
            const rInB = f.getResidentsInBuilding();
            f.tenantsChecked = rInB.some(r => (r.role === 'rent' && r.checked));
            f.ownerOnChecked = rInB.some(r => (r.role === 'owner_on' && r.checked));
            f.ownerOffChecked = rInB.some(r => (r.role === 'owner_off' && r.checked));
            f.residentOwnerChecked = rInB.some(r => ( r.role === 'resident_owner' && r.checked));
            f.residentOnlyNotOnDeedChecked = rInB.some(r => (r.role === 'resident_only_not_on_deed' && r.checked));
            f.ownerFamilyChecked = rInB.some(r => (r.role === 'owner_family' && r.checked));
            f.guestChecked = rInB.some(r => (r.role === 'guest' && r.checked));
            f.offsiteResidentChecked = rInB.some(r => (r.role === 'offsite_resident' && r.checked));
            f.residentChecked = rInB.some(r => (r.role === 'resident' && r.checked));
            f.occupantChecked = rInB.some(r => (r.role === 'occupant' && r.checked));
            f.notSetChecked = rInB.some(r => (r.role === 'unknown' && r.checked));
            f.groups.map(g => {
                g.checked = rInB.some(r => (
                    r.checked &&
                    r.groups.some(rg => g.name === rg.itemName)
                ));
            });
            f.staffChecked = rInB.some(r => (r.role === 'staff' && r.checked));
            f.unitManagerChecked = rInB.some(r => (r.role === 'unit_manager' && r.checked));
        });
    }
}

export interface FilterStateItem {
    buildingId: number;
    buildingName: string;
    tenantsChecked: boolean;
    tenantsHalfChecked: boolean;
    tenantsCount: number;
    ownerOnChecked: boolean;
    ownerOnHalfChecked: boolean;
    ownerOnCount: number;
    ownerOffChecked: boolean;
    ownerOffHalfChecked: boolean;
    ownerOffCount: number;
    residentOwnerChecked: boolean;
    residentOwnerHalfChecked: boolean;
    residentOwnerCount: number;
    residentOnlyNotOnDeedChecked: boolean;
    residentOnlyNotOnDeedHalfChecked: boolean;
    residentOnlyNotOnDeedCount: number;
    ownerFamilyChecked: boolean;
    ownerFamilyHalfChecked: boolean;
    ownerFamilyCount: number;
    guestChecked: boolean;
    guestHalfChecked: boolean;
    guestCount: number;
    offsiteResidentChecked: boolean;
    offsiteResidentHalfChecked: boolean;
    offsiteResidentCount: number;
    residentChecked: boolean;
    residentHalfChecked: boolean;
    residentCount: number;
    occupantChecked: boolean;
    occupantHalfChecked: boolean;
    occupantCount: number;
    notSetChecked: boolean;
    notSetHalfChecked: boolean;
    notSetCount: number;
    staffChecked: boolean;
    staffHalfChecked: boolean;
    staffCount: number;
    unitManagerChecked: boolean;
    unitManagerHalfChecked: boolean;
    unitManagerCount: number;
    groups: [{ name: string, count: number, halfchecked: boolean }];
    checkedResidents: number[];
}

export class FilterRecipientsItem {
    parent: FilterRecipients;
    buildingId: number;
    buildingName: string;
    cNSettings: any = null;
    showDropdown = false;

    tenantsChecked = false;
    ownerOnChecked = false;
    ownerOffChecked = false;
    residentOwnerChecked = false;
    residentOnlyNotOnDeedChecked = false;
    ownerFamilyChecked = false;
    guestChecked = false;
    offsiteResidentChecked = false;
    residentChecked = false;
    occupantChecked = false;
    notSetChecked = false;
    staffChecked = false;
    unitManagerChecked = false;

    tenantsShow = false;
    ownerOnShow = false;
    ownerOffShow = false;
    residentOwnerShow = false;
    residentOnlyNotOnDeedShow = false;
    ownerFamilyShow = false;
    guestShow = false;
    offsiteResidentShow = false;
    residentShow = false;
    occupantShow = false;
    notSetShow = false;
    staffShow = false;
    unitManagerShow = false;

    groups: { name: string, checked: boolean, show: boolean }[] = [];
    residentsLoadPromise: Promise<any>;
    disabled = false;
    isCanEdit: boolean = true;
    isCanDelete: boolean = true;
    isHidden: boolean = false;

    residentsWithoutEmail: Array<any> = [];
    occupancyTypes: Array<any> = [];

    constructor(buildingId: number,
                buildingName: string,
                groups: Group[],
                parent: FilterRecipients,
                cNSettings: any = null) {
        this.buildingId = buildingId;
        this.buildingName = buildingName;
        this.cNSettings = cNSettings;
        this.parent = parent;
        this.groups = groups.map(g => ({name: g.itemName, checked: false, show: false}));
        this.isCanEdit = parent.es.account.canUpdate(parent.es.serviceName, buildingId);
        this.isCanDelete = parent.es.account.canDelete(parent.es.serviceName, buildingId);
    }

    /**
     * Reset checked state and visibility
     * to defaults
     */
    public forceReset(): void {
        this.tenantsShow = this.ownerOnShow = this.ownerOffShow = this.notSetShow = this.staffShow =
            this.residentOwnerShow = this.residentOnlyNotOnDeedShow = this.ownerFamilyShow = this.guestShow =
                this.offsiteResidentShow = this.residentShow = this.occupantShow = this.unitManagerShow = false;

        this.groups.map(group => {
            group.show = false;
            group.checked = false;
        });

        this.tenantsChecked = this.ownerOnChecked = this.ownerOffChecked = this.notSetChecked = this.staffChecked =
            this.residentOwnerChecked = this.residentOnlyNotOnDeedChecked = this.ownerFamilyChecked = this.guestChecked =
                this.offsiteResidentChecked = this.residentChecked = this.occupantChecked = this.unitManagerChecked = false;

    }
  
    private loadOccupancyTypes() { 
        if (!this.occupancyTypes.length) { 
            const url = `/v1/all_occupancy_types.json`;
            this.parent.es.getAllOccupancyTypes('domainBD', url).subscribe(result => {        
                this.occupancyTypes = result.value.entities.slice(); 
            });
        }
    }

    private loadResidents(): Promise<any> {
        if (!this.residentsLoadPromise) {
            this.residentsLoadPromise = new Promise((resolve, reject) => {
                this.parent.es.spinnerShow = true;
                this.disabled = true;
                const url = `/v1/suite-users/g_emshort.json?expand=user.groups.group,user.phones,suite&is_valid_email=1&suite_building=${this.buildingId}&order-by=suite.suiteNumber|asc`;

                this.parent.es.api.getInfiniteList('domainBD', url, 1500).subscribe(result => {
                    this.residentsWithoutEmail = [];
                    result.entities.forEach((suiteUser) => {
                        if (!suiteUser.user) {
                            return;
                        }

                        const resident = suiteUser.user;
                        const recipient = new ResidentEmailRecipient();

                        recipient.id = resident.id;

                        if (resident && !resident.email) {
                            if (suiteUser.role) recipient.role = suiteUser.role;
                            if (suiteUser.building) recipient.buildingId = suiteUser.building;

                            this.residentsWithoutEmail.push(recipient);

                            return;
                        } else {
                            recipient.email = resident.email;
                        }

                        if (resident.fullName) recipient.fullName = resident.fullName;
                        if (suiteUser.role) recipient.role = suiteUser.role;
                        if (resident.emailWaiverSigned) recipient.emailWaiverSigned = !!resident.emailWaiverSigned;
                        if (resident.phones) recipient.phones = resident.phones;

                        if (suiteUser.suite) {
                            recipient.suiteId = suiteUser.suite.id;
                            recipient.suiteNumber = suiteUser.suite.suiteNumber;
                            recipient.commonElementsFee = suiteUser.suite.commonElementsFee;
                        }

                        if (suiteUser.building) {
                            recipient.buildingId = suiteUser.building;
                            const b = this.parent.es.buildings.find(_b => _b.id == suiteUser.building);
                            recipient.buildingName = b.name;
                            recipient.buildingAddress = b.address;
                        }

                        recipient.groups = resident.groups.map(item => ({id: item.group.id, itemName: item.group.name}));

                        const isSet: boolean = !!this.parent.residents.find((item) => {
                            return item.id === recipient.id;
                        });

                        if (!isSet) {
                            this.parent.residents.push(recipient);
                        }
                    });

                    if(this.parent.orderBy) {
                        const fieldName = this.parent.orderBy.split('|')[0];
                        const order = this.parent.orderBy.split('|')[1];
                        this.parent.residents.sort((a: any, b: any) => {
                            if (a[fieldName] < b[fieldName]) {
                                return order == 'asc' ? -1 : 1;
                            } else if (a[fieldName] > b[fieldName]) {
                                return order == 'asc' ? 1 : -1;
                            } else {
                                return 0;
                            }
                        });
                    }

                    const userRolesUrl = `/api/users?expand=company,phones,lastActivity&page=1&per-page=1000&building_id=${this.buildingId}&order-by=id|asc&user_type[0]=property_manager`;
                    this.parent.es.api.get('oauthUrl', userRolesUrl).subscribe(
                        res => {
                            JSON.parse(res._body).entities.forEach(item => {
                                let staff = new ResidentEmailRecipient();
                                staff.id = item.id;
                                staff.email = item.email;
                                staff.fullName = item.fullName;
                                staff.role = 'staff';
                                staff.buildingId = this.buildingId;
                                staff.buildingName = this.buildingName;
                                staff.emailWaiverSigned = true;
                                this.parent.residents.push(staff);
                            });

                            this.parent.es.spinnerShow = false;
                            this.disabled = false;
                            resolve();
                        }, error => this.parent.es.api.errorHandler(error, 'Failed to get Staff users')
                    );
                }, error => {
                    reject();
                    this.parent.es.api.errorHandler(error, 'Failed get Residents');
                    this.disabled = false;
                });
            });
        }

        return this.residentsLoadPromise;
    }

    getResidentsInBuilding(): ResidentEmailRecipient[] {
        return this.parent.residents
            .filter(r => (
                (r.buildingId === this.buildingId) &&
                (r.emailWaiverSigned || this.parent.ignoreWaiverSettings)
            )) || [];
    }

    getUsersCount(role: string): number {
        return this.parent.residents.filter(r => (
            r.buildingId == this.buildingId && r.role === role &&
            (r.emailWaiverSigned || this.parent.ignoreWaiverSettings)
        )).length;
    }

    getTotalUsersCount(role: string): number {
        return this.parent.residents.filter(r => r.buildingId == this.buildingId && r.role === role).length;
    }

    getUsersCountWithoutEmail(role: string): number {
        return this.residentsWithoutEmail.filter(item => {
            return item.buildingId == this.buildingId && item.role === role;
        }).length;
    }

    getUsersCheckedCount(role: string): number {
        return this.parent.residents
            .filter(r => (
                    r.buildingId == this.buildingId &&
                    r.checked && r.role === role &&
                    (r.emailWaiverSigned || this.parent.ignoreWaiverSettings)
                )
            ).length;
    }

    getUserInGroupCount(groupName: string): number {
        return this.parent.residents.filter(r => {
            return (
                r.buildingId == this.buildingId &&
                r.groups.some(group => group.itemName === groupName) &&
                (r.emailWaiverSigned || this.parent.ignoreWaiverSettings)
            );
        }).length;
    }

    getTotalUserInGroupCount(groupName: string): number {
        return this.parent.residents.filter(r => {
            return (
                r.buildingId == this.buildingId &&
                r.groups.some(group => group.itemName === groupName)
            );
        }).length;
    }

    getUserCheckedInGroupCount(groupName: string): number {
        return this.parent.residents.filter(r => {
            return (
                r.buildingId == this.buildingId &&
                r.checked &&
                r.groups.some(group => group.itemName === groupName) &&
                (r.emailWaiverSigned || this.parent.ignoreWaiverSettings)
            );
        }).length;
    }

    isAllCheckboxChecked(): boolean {
        return (
            this.tenantsChecked &&
            this.ownerOnChecked &&
            this.ownerOffChecked &&
            this.residentOwnerChecked &&
            this.residentOnlyNotOnDeedChecked &&
            this.ownerFamilyChecked &&
            this.guestChecked &&
            this.offsiteResidentChecked &&
            this.residentChecked &&
            this.occupantChecked &&
            this.notSetChecked &&
            this.groups.every(g => g.checked) &&
            this.staffChecked &&
            this.unitManagerChecked
        );
    }

    getGroupFilterState() {
        const result = [];
        this.groups.filter(g => g.checked).forEach(g => {
            const gObj = {
                name: g.name,
                count: this.getUserCheckedInGroupCount(g.name),
                halfchecked: this.isHalfCheckedClass('group', g.name)
            };
            result.push(gObj);
        });
        return result;
    }

    toggleTargetList(target: string, group?: string) {
        this.loadOccupancyTypes();
        this.loadResidents().then(() => {
            if (target === 'all') {
                this.tenantsShow = this.ownerOnShow = this.ownerOffShow = this.notSetShow = this.residentOwnerShow =
                    this.residentOnlyNotOnDeedShow = this.ownerFamilyShow = this.guestShow = this.offsiteResidentShow =
                        this.residentShow = this.occupantShow = this.staffShow = this.unitManagerShow = true;
                this.groups.map(g => g.show = true);

            } else if (target === 'residents') {
                this.tenantsShow = this.ownerOnShow = true;

            } else if (target === 'group') {
                this.groups.find(g => g.name === group).show = true;

            } else if(target === 'staff') {
                this.staffShow = true;
            }  else {
                this[target] = true;
            }

            this.parent.updateFilteredResidentsList();
        });
    }

    unselectTargetList(target: string, group?: string) {
        this.loadResidents().then(() => {

            if (target === 'all') {
                this.tenantsShow = this.ownerOnShow = this.ownerOffShow = this.notSetShow = this.residentOwnerShow =
                    this.residentOnlyNotOnDeedShow = this.ownerFamilyShow = this.guestShow =
                        this.offsiteResidentShow = this.residentShow = this.occupantShow = this.staffShow = this.unitManagerShow = false;
                this.groups.map(g => g.show = false);

            } else if (target === 'residents') {
                this.tenantsShow = this.ownerOnShow = false;

            } else if (target === 'group') {
                this.groups.find(g => g.name === group).show = false;

            } else if(target === 'staff') {
                this.staffShow = false;
            }  else {
                this[target] = false;
            }

            this.parent.updateFilteredResidentsList();
        });
    }

    isAllInBuildingShow() {
        return (
            this.tenantsShow &&
            this.ownerOnShow &&
            this.ownerOffShow &&
            this.residentOwnerShow &&
            this.residentOnlyNotOnDeedShow &&
            this.ownerFamilyShow &&
            this.guestShow &&
            this.offsiteResidentShow &&
            this.residentShow &&
            this.occupantShow &&
            this.notSetShow &&
            this.groups.every(g => g.show) &&
            this.staffShow &&
            this.unitManagerShow
        );
    }

    checkedTargetResidents(target: string, state: boolean, targetGroupName?: string) {
        this.loadResidents().then(() => {
            let targetResident;

            if (target === 'all') {
                targetResident = this.getResidentsInBuilding();
                this.tenantsChecked = this.ownerOnChecked = this.ownerOffChecked = this.notSetChecked = this.staffChecked =
                    this.residentOwnerChecked = this.residentOnlyNotOnDeedChecked = this.ownerFamilyChecked =
                    this.guestChecked = this.offsiteResidentChecked = this.residentChecked = this.occupantChecked = this.unitManagerChecked = state;
                this.groups.map(g => g.checked = state);

            } else if (target === 'residents') {
                this.tenantsChecked = this.ownerOnChecked = state;
                targetResident = this.getResidentsInBuilding()
                    .filter(r => (r.role === 'rent' || r.role === 'owner_on'));

            } else if (target === 'group') {
                targetResident = this.getResidentsInBuilding()
                    .filter(r => r.groups.some(g => targetGroupName === g.itemName));

            } else if(target === 'staff') {
                this.staffChecked = state;
            } else {
                targetResident = this.getResidentsInBuilding().filter(r => r.role === target);
            }

            if (targetResident) targetResident.map(g => g.checked = state);
        });
    }

    isHalfCheckedClass(target: string, targetGroupName?: string): boolean {
        let targetResident;

        if (target === 'all') {
            targetResident = this.getResidentsInBuilding();

        } else if (target === 'residents') {
            targetResident = this.getResidentsInBuilding()
                .filter(r => (r.role === 'rent' || r.role === 'owner_on'));

        } else if (target === 'group') {
            targetResident = this.getResidentsInBuilding()
                .filter(r => r.groups.some(g => targetGroupName === g.itemName));

        } else if(target === 'staff') {
            targetResident = this.getResidentsInBuilding().filter(r => r.role === 'staff');
        } else {
            targetResident = this.getResidentsInBuilding().filter(r => r.role === target);
        }

        const checkedResidents = targetResident.filter(r => r.checked);

        return (checkedResidents.length && targetResident.length !== checkedResidents.length);
    }

  toggleTargetListAndCheckTargetResidents(targetResidentType: string, targetCheckboxName: string, state: boolean, group?: string) {
        this.loadResidents().then(() => {
            let targetResident;

            if (targetCheckboxName === 'all') {
                targetResident = this.getResidentsInBuilding();
                this.tenantsChecked = this.ownerOnChecked = this.ownerOffChecked = this.notSetChecked = this.staffChecked =
                    this.residentOwnerChecked = this.residentOnlyNotOnDeedChecked = this.ownerFamilyChecked =
                    this.guestChecked = this.offsiteResidentChecked = this.residentChecked = this.occupantChecked = this.unitManagerChecked = state;
                this.groups.map(g => g.checked = state);

            } else if (targetCheckboxName === 'residents') {
                this.tenantsChecked = this.ownerOnChecked = state;
                targetResident = this.getResidentsInBuilding()
                    .filter(r => (r.role === 'rent' || r.role === 'owner_on'));

            } else if (targetCheckboxName === 'group') {
                targetResident = this.getResidentsInBuilding()
                    .filter(r => r.groups.some(g => group === g.itemName));

            } else if(targetCheckboxName === 'staff') {
                this.staffChecked = state;
                targetResident = this.getResidentsInBuilding().filter(r => r.role === 'staff');
            } else {
                targetResident = this.getResidentsInBuilding().filter(r => r.role === targetCheckboxName);
            }

            if (targetResident) targetResident.map(g => g.checked = state);

            if (targetResidentType === 'all') {
                this.tenantsShow = this.ownerOnShow = this.ownerOffShow = this.notSetShow = this.residentOwnerShow =
                    this.residentOnlyNotOnDeedShow = this.ownerFamilyShow = this.guestShow = this.offsiteResidentShow =
                        this.residentShow = this.occupantShow = this.staffShow = this.unitManagerShow = true;
                this.groups.map(g => g.show = true);

            } else if (targetResidentType === 'residents') {
                this.tenantsShow = this.ownerOnShow = true;

            } else if (targetResidentType === 'group') {
                this.groups.find(g => g.name === group).show = true;

            } else if(targetResidentType === 'staff') {
                this.staffShow = true;
            } else {
                this[targetResidentType] = true;
            }

            this.parent.updateFilteredResidentsList();
        });
    }
}



// WEBPACK FOOTER //
// /app/src/app/services/filter-recipients.ts